import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { useState } from "react";
import axios from "../../../lib/axios";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";

// ─── Asset Types ──────────────────────────────────────────────────────────────

export const useAssetTypes = () =>
  useQuery<any, Error>({
    queryKey: [API_ENDPOINTS.ASSET_TYPE_LIST],
    queryFn: () => axios.get(API_ENDPOINTS.ASSET_TYPE_LIST).then((r) => r.data),
    staleTime: 60 * 1000,
  });

export const useAssetTypeCreate = (options?: { onSuccess?: () => void; onError?: (e: any) => void }) => {
  const queryClient = useQueryClient();
  return useMutation<any, Error, any>({
    mutationFn: (data) => axios.post(API_ENDPOINTS.ASSET_TYPE_CREATE, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.ASSET_TYPE_LIST] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

export const useAssetTypeUpdate = (options?: { onSuccess?: () => void; onError?: (e: any) => void }) => {
  const queryClient = useQueryClient();
  return useMutation<any, Error, { id: number; [key: string]: any }>({
    mutationFn: ({ id, ...data }) => axios.put(`${API_ENDPOINTS.ASSET_TYPE_UPDATE}/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.ASSET_TYPE_LIST] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

export const useAssetTypeDelete = (options?: { onSuccess?: () => void; onError?: (e: any) => void }) => {
  const queryClient = useQueryClient();
  return useMutation<any, Error, number>({
    mutationFn: (id) => axios.delete(`${API_ENDPOINTS.ASSET_TYPE_DELETE}/${id}`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.ASSET_TYPE_LIST] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

// ─── Assets ───────────────────────────────────────────────────────────────────

export const useAssetList = (initialParams: any = { page: 1, limit: 20 }) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery<any, Error>({
    queryKey: [API_ENDPOINTS.ASSET_LIST, params],
    queryFn: () => axios.get(API_ENDPOINTS.ASSET_LIST, { params }).then((r) => r.data),
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const useAssetDetail = (id: number | null) =>
  useQuery<any, Error>({
    queryKey: [API_ENDPOINTS.ASSET_DETAIL, id],
    queryFn: () => axios.get(`${API_ENDPOINTS.ASSET_DETAIL}/${id}`).then((r) => r.data),
    enabled: !!id,
    staleTime: 30 * 1000,
  });

export const useAssetCreate = (options?: { onSuccess?: (d: any) => void; onError?: (e: any) => void }) => {
  const queryClient = useQueryClient();
  return useMutation<any, Error, any>({
    mutationFn: (data) => axios.post(API_ENDPOINTS.ASSET_CREATE, data).then((r) => r.data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.ASSET_LIST] });
      options?.onSuccess?.(res.data);
    },
    onError: options?.onError,
  });
};

export const useAssetUpdate = (options?: { onSuccess?: (d: any) => void; onError?: (e: any) => void }) => {
  const queryClient = useQueryClient();
  return useMutation<any, Error, any>({
    mutationFn: (data) => axios.post(API_ENDPOINTS.ASSET_UPDATE, data).then((r) => r.data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.ASSET_LIST] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.ASSET_DETAIL] });
      options?.onSuccess?.(res.data);
    },
    onError: options?.onError,
  });
};

export const useAssetDelete = (options?: { onSuccess?: () => void; onError?: (e: any) => void }) => {
  const queryClient = useQueryClient();
  return useMutation<any, Error, number>({
    mutationFn: (id) => axios.post(API_ENDPOINTS.ASSET_DELETE, { id }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.ASSET_LIST] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

// ─── Asset Service History ─────────────────────────────────────────────────────

export const useAssetServiceHistory = (assetId: number | null) =>
  useQuery<any, Error>({
    queryKey: [API_ENDPOINTS.ASSET_SERVICE_HISTORY, assetId, "service-history"],
    queryFn: () => axios.get(`${API_ENDPOINTS.ASSET_SERVICE_HISTORY}/${assetId}/service-history`).then((r) => r.data),
    enabled: !!assetId,
    staleTime: 30 * 1000,
  });

export const useAssetAddService = (
  assetId: number | null,
  options?: { onSuccess?: () => void; onError?: (e: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation<any, Error, any>({
    mutationFn: (data) =>
      axios.post(`${API_ENDPOINTS.ASSET_SERVICE_HISTORY}/${assetId}/service-history`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.ASSET_SERVICE_HISTORY, assetId, "service-history"] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.ASSET_DETAIL, assetId] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.ASSET_LIST] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

// ─── Expiring Assets ──────────────────────────────────────────────────────────

export const useExpiringAssets = (params?: { days?: number; page?: number; limit?: number }) =>
  useQuery<any, Error>({
    queryKey: [API_ENDPOINTS.ASSET_EXPIRING, params],
    queryFn: () => axios.get(API_ENDPOINTS.ASSET_EXPIRING, { params }).then((r) => r.data),
    staleTime: 60 * 1000,
  });

// ─── Industry Seed ────────────────────────────────────────────────────────────

export const useIndustrySeedSlugs = () =>
  useQuery<any, Error>({
    queryKey: [API_ENDPOINTS.INDUSTRY_SEED_SLUGS],
    queryFn: () => axios.get(API_ENDPOINTS.INDUSTRY_SEED_SLUGS).then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

export const useIndustrySeed = (options?: { onSuccess?: (d: any) => void; onError?: (e: any) => void }) =>
  useMutation<any, Error, { industry_slug: string }>({
    mutationFn: (data) => axios.post(API_ENDPOINTS.INDUSTRY_SEED, data).then((r) => r.data),
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
